import React, { useState } from "react";
import { AddToCart } from "./AddToCart";
import { ScentSelector } from "./ScentSelector";
import { PdpTab } from "./PdpTab";
import {
  usePdpFocusedScent,
  usePdpScents,
  usePdpProduct,
  usePdpSellingPlan,
  usePdpVariant,
} from "./PdpContext";
import { Button } from "@/components/Button";
import { getFinalAtcCtaPrice } from "@/utils/atcCTAPrice";
import { DroppsVariant, variantSelect, productSelect } from "@/store";
import {
  MoneyBackIcon,
  FreeShippingIcon,
  DollarCircleIcon,
  FreeShippingTrialProductIcon,
} from "@/components/icons";
import { getEffectiveSubscriptionDiscountPercent } from "@/helpers/constants";
import { growthBook } from "@/helpers/growthbook";

export interface CartFlowProps {
  cartAddId: string;
}

function CartFlow({ cartAddId }: CartFlowProps) {
  const focusedScent = usePdpFocusedScent();
  const { pdpTabData, pdpIngredientSection, tags } = usePdpProduct();
  const scents = usePdpScents();
  const [toggleScentOpen, setToggleScentOpen] = useState(false);
  const product = usePdpProduct();
  const variant = usePdpVariant();
  const sellingPlan = usePdpSellingPlan();
  const subscription = variantSelect.orderableSubscription(variant);
  const isBulk = product.title.toLocaleLowerCase().includes("bulk");
  const isBundle = product?.type === "Bundle";
  const isTrialProduct = tags?.includes("trial-product");
  const pdpShowScentTest = growthBook.getFeatureValue("show-scents-by-default", 0);

  const productPrice = getFinalAtcCtaPrice({
    productCollections: product.collections,
    basePriceCents: sellingPlan
      ? subscription?.discount.price.asCents()
      : variant.price.asCents(),
    sellingPlan,
    subscription,
    variant,
    product,
    currency: "$",
    strikeThroughSize: "text-base",
    priceSize: "text-xl",
    strikeThroughColor: "text-primary",
    priceColor: "text-primary",
  });

  return (
    <div className="grid gap-4">
      {
        <div>
          <div className="flex items-center space-x-2">
            <span className="text-[28px] font-bold leading-tight text-black">
              {productPrice.currentPrice}
            </span>
            {variant?.package_quantity &&
              product?.type !== "Bundle" &&
              !isBulk && (
                <span className="text-xs text-black">
                  (
                  {(() => {
                    // Calculate per-pod price with global discount utility
                    let priceCents =
                      sellingPlan && subscription
                        ? subscription.discount.price.asCents()
                        : variant.price.asCents();
                    if (
                      sellingPlan &&
                      subscription &&
                      subscription.discount.kind === "percentage"
                    ) {
                      const percent = getEffectiveSubscriptionDiscountPercent(
                        subscription.discount.percentage,
                        { isBulk, isBundle }
                      );
                      priceCents = variant.price
                        .mul(1 - percent / 100)
                        .asCents();
                    }
                    const perPod = priceCents / variant.package_quantity / 100;
                    return `$${perPod.toFixed(2)}/pod`;
                  })()}
                  )
                </span>
              )}
          </div>
          {product?.type != "Bundle" &&
            productPrice.currentPrice != variant.price.format() && (
              <div className="mt-2 text-xs leading-tight text-black">
                List price:{" "}
                <span className="line-through">{variant.price.format()}</span>
              </div>
            )}
          {product?.type === "Bundle" && variant.compareAtPrice && (
            <div className="mt-2 text-xs leading-tight text-black">
              List price:{" "}
              <span className="line-through">
                {variant.compareAtPrice.format()}
              </span>
            </div>
          )}
        </div>
      }
      {focusedScent !== undefined && pdpShowScentTest === 0 ? (
        <div className="mb-4">
          <div
            className={`mt-2 flex items-center space-x-2 ${
              toggleScentOpen ? "mb-4" : ""
            }`}
          >
            <div
              className="font-myriad-pro-condensed max-w-fit rounded-full px-4 py-2 text-xl font-semibold uppercase text-black"
              style={{ backgroundColor: focusedScent?.color }}
            >
              {focusedScent?.name}
            </div>
            <Button
              variant="text"
              onClick={() => {
                setToggleScentOpen(!toggleScentOpen);
              }}
              style={{ color: "#000" }}
              className="font-semibold normal-case tracking-normal underline"
            >
              {scents.length === 1
                ? toggleScentOpen
                  ? "Hide details"
                  : "Show details"
                : toggleScentOpen
                ? "Hide scent"
                : "Show scent"}
            </Button>
          </div>
          {toggleScentOpen && (
            <div className="px-4">
              <ScentSelector
                focusedScent={focusedScent}
                showBottomPart={false}
              />
              <p className="m-0 text-sm text-gray-700">
                {focusedScent?.description}
              </p>
            </div>
          )}
        </div>
      ) : null}

      {focusedScent !== undefined && pdpShowScentTest === 1 ? (
        <div className="mb-4 mt-2">
            <ScentSelector
              focusedScent={focusedScent}
              showBottomPart={false}
              pdpShowScentTestVariant={true}
            />
        </div>
      ) : null}

      <AddToCart cartSubmitId={cartAddId} />

      {pdpTabData &&
        (pdpTabData.overview ||
          pdpTabData.impact ||
          pdpTabData.ingredients) && (
          <PdpTab
            tabData={pdpTabData}
            ingredientTabDrawerData={pdpIngredientSection}
          />
        )}

      <div className="flex justify-center space-x-4 pb-4 pt-0 sm:pb-2 sm:pt-2">
        <div className="flex items-center space-x-2">
          {isTrialProduct ? (
            <>
              <DollarCircleIcon />
              <p className="body text-sm">
                <strong>25% off</strong> all recurring orders of these products
              </p>
            </>
          ) : (
            <>
              <MoneyBackIcon />
              <p className="body text-sm">
                <strong>30-day</strong> money back guarantee
              </p>
            </>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {isTrialProduct ? (
            <>
              <FreeShippingTrialProductIcon />
              <p className="body text-sm">
                <strong>Free Shipping</strong> on this starter set!
              </p>
            </>
          ) : (
            <>
              <FreeShippingIcon />
              <p className="body text-sm">
                <strong>Free Shipping</strong> on orders over $35
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CartFlow;
